import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider } from 'react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { router } from './router';
import { queryClient } from './lib/queryClient';
import { routesFor } from './lib/feature-registry';
import type { FrontendKey } from '@septima/contract';
import './globals.css';
// Workflow editor styles. Imported AFTER globals.css so the package's `--wf-*`
// vars resolve against the dashboard `--color-*` tokens globals.css defines.
import '@septima/workflow-ui/styles.css';

// React Grab — dev-only. Hover any UI element and press ⌘C to copy its source
// location + component stack for a coding agent. The `import.meta.env.DEV` guard
// means Vite tree-shakes this dynamic import out of the production build, so it
// never ships in the `aws s3 sync`'d bundle.
if (import.meta.env.DEV) {
    void import('react-grab');
}

const root = document.getElementById('root');
if (!root) throw new Error('No #root element');

// The page modules of the current view, from the routes of the enabled
// features (ADR-0027). A disabled feature has no route in the build, so its
// pages are never loaded here.
const STAFF_SURFACES: readonly FrontendKey[] = ['desk', 'ops', 'comply', 'view'];

function currentSurface() {
    return window.location.pathname.split('/').filter(Boolean)[0];
}

function preloadStaffPages() {
    const surface = STAFF_SURFACES.find((key) => key === currentSurface());
    if (!surface) return;

    const loaders = routesFor(surface).flatMap((route) => (route.lazy ? [route.lazy] : []));
    let index = 0;
    const loadBatch = () => {
        for (let count = 0; count < 3 && index < loaders.length; count += 1) {
            void loaders[index++]();
        }
        if (index < loaders.length) {
            window.setTimeout(loadBatch, 120);
        }
    };
    if (window.requestIdleCallback) {
        window.requestIdleCallback(() => loadBatch(), { timeout: 1_500 });
    } else {
        window.setTimeout(loadBatch, 800);
    }
}

ReactDOM.createRoot(root).render(
    <React.StrictMode>
        <QueryClientProvider client={queryClient}>
            <RouterProvider router={router} />
        </QueryClientProvider>
    </React.StrictMode>,
);

preloadStaffPages();
